/**
 * Billing Page Constants
 * Constants for billing and subscription management
 */
import { tr } from '../i18n/i18n';

/**
 * Tab options for the billing page
 */
export enum BILLING_TAB {
  PLANS = 'plans',
  BILLING = 'billing',
}

/**
 * Type for billing tab values
 */
export type BillingTabType = `${BILLING_TAB}`;

/**
 * Modal actions for student fees page
 * Used to queue modal openings after state updates
 */
export enum PAYMENT_MODAL_ACTION {
  COLLECT = 'collect',
  REVOKE = 'revoke',
  CANCEL = 'cancel',
  DELETE = 'delete',
  REFUND = 'refund',
}

/**
 * Per-request timeout for the student-invoice PDF, overriding the shared axios
 * instance's 10s default. The PDF is rendered server-side, and a cold render
 * can outlast 10s while the file is still being produced.
 */
export const INVOICE_PDF_TIMEOUT_MS = 60_000;

/**
 * Invoice Status Enum
 * Matches backend INVOICE_STATUS enum values exactly
 */
export enum INVOICE_STATUS {
  PENDING = 'Pending',
  PAID = 'Paid',
  OVERDUE = 'Overdue',
  OVERDUE_PARTIAL = 'Overdue Partial',
  PARTIAL = 'Partial',
  CANCELLED = 'Cancelled',
}

/**
 * Raw invoice payment status values returned and accepted by the backend API.
 */
export enum INVOICE_STATUS_API {
  PENDING = 'PENDING',
  PAID = 'PAID',
  OVERDUE = 'OVERDUE',
  OVERDUE_PARTIAL = 'OVERDUE_PARTIAL',
  PARTIAL = 'PARTIAL',
  CANCELLED = 'CANCELLED',
}

export const INVOICE_STATUS_LABELS: Record<INVOICE_STATUS_API, INVOICE_STATUS> = {
  [INVOICE_STATUS_API.PENDING]: INVOICE_STATUS.PENDING,
  [INVOICE_STATUS_API.PAID]: INVOICE_STATUS.PAID,
  [INVOICE_STATUS_API.OVERDUE]: INVOICE_STATUS.OVERDUE,
  [INVOICE_STATUS_API.OVERDUE_PARTIAL]: INVOICE_STATUS.OVERDUE_PARTIAL,
  [INVOICE_STATUS_API.PARTIAL]: INVOICE_STATUS.PARTIAL,
  [INVOICE_STATUS_API.CANCELLED]: INVOICE_STATUS.CANCELLED,
};

export const INVOICE_STATUS_COLORS: Record<INVOICE_STATUS_API, string> = {
  [INVOICE_STATUS_API.PENDING]: 'text-yellow-500',
  [INVOICE_STATUS_API.PAID]: 'text-green-600',
  [INVOICE_STATUS_API.OVERDUE]: 'text-red-600 font-bold',
  [INVOICE_STATUS_API.OVERDUE_PARTIAL]: 'text-red-600 font-bold',
  // Amber, to match the mobile app's partial-status colour (Colors.amberDark #B45309).
  [INVOICE_STATUS_API.PARTIAL]: 'text-amber-700',
  [INVOICE_STATUS_API.CANCELLED]: 'text-red-600',
};

export const normalizeInvoiceStatus = (status?: string | null): INVOICE_STATUS_API | null => {
  if (!status) return null;

  const normalized = status.toUpperCase();
  if (normalized in INVOICE_STATUS_API) {
    return INVOICE_STATUS_API[normalized as keyof typeof INVOICE_STATUS_API];
  }

  const apiStatus = Object.entries(INVOICE_STATUS_LABELS).find(
    ([, label]) => label.toLowerCase() === status.toLowerCase(),
  )?.[0];

  return (apiStatus as INVOICE_STATUS_API | undefined) ?? null;
};

/**
 * Payment Status Enum
 * Calculated based on amount entered vs balance due
 * Used for dynamic UI rendering in payment modals
 */
export enum PAYMENT_STATUS {
  PARTIAL = 'partial',
  EXACT = 'exact',
  OVERPAYMENT = 'overpayment',
}

/**
 * Days of week options for Weekly frequency scheduling
 */
export const DAY_OF_WEEK_OPTIONS = [
  { label: tr('common.day_monday', 'Monday'), value: 0 },
  { label: tr('common.day_tuesday', 'Tuesday'), value: 1 },
  { label: tr('common.day_wednesday', 'Wednesday'), value: 2 },
  { label: tr('common.day_thursday', 'Thursday'), value: 3 },
  { label: tr('common.day_friday', 'Friday'), value: 4 },
  { label: tr('common.day_saturday', 'Saturday'), value: 5 },
  { label: tr('common.day_sunday', 'Sunday'), value: 6 },
];

/**
 * Month options for Yearly frequency scheduling
 */
export const MONTH_OPTIONS = [
  { label: tr('common.month_january', 'January'), value: 1 },
  { label: tr('common.month_february', 'February'), value: 2 },
  { label: tr('common.month_march', 'March'), value: 3 },
  { label: tr('common.month_april', 'April'), value: 4 },
  { label: tr('common.month_may', 'May'), value: 5 },
  { label: tr('common.month_june', 'June'), value: 6 },
  { label: tr('common.month_july', 'July'), value: 7 },
  { label: tr('common.month_august', 'August'), value: 8 },
  { label: tr('common.month_september', 'September'), value: 9 },
  { label: tr('common.month_october', 'October'), value: 10 },
  { label: tr('common.month_november', 'November'), value: 11 },
  { label: tr('common.month_december', 'December'), value: 12 },
];

/**
 * Tab options for invoice management page
 */
export enum INVOICE_MANAGEMENT_TAB {
  ASSIGN_INVOICES = 0,
  INVOICES = 1,
}

/**
 * Invoice Lifecycle Status Enum
 * Matches backend INVOICE_LIFECYCLE_STATUS Prisma enum
 */
export enum INVOICE_LIFECYCLE_STATUS {
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

/**
 * Display labels for invoice lifecycle statuses
 */
export const INVOICE_LIFECYCLE_STATUS_LABELS: Record<INVOICE_LIFECYCLE_STATUS, string> = {
  [INVOICE_LIFECYCLE_STATUS.ACTIVE]: 'Active',
  [INVOICE_LIFECYCLE_STATUS.PAUSED]: 'Paused',
  [INVOICE_LIFECYCLE_STATUS.COMPLETED]: 'Completed',
  [INVOICE_LIFECYCLE_STATUS.CANCELLED]: 'Cancelled',
};

/**
 * Color classes for invoice lifecycle statuses
 */
export const INVOICE_LIFECYCLE_STATUS_COLORS: Record<INVOICE_LIFECYCLE_STATUS, string> = {
  [INVOICE_LIFECYCLE_STATUS.ACTIVE]: 'text-green-600',
  [INVOICE_LIFECYCLE_STATUS.PAUSED]: 'text-yellow-600',
  [INVOICE_LIFECYCLE_STATUS.COMPLETED]: 'text-blue-600',
  [INVOICE_LIFECYCLE_STATUS.CANCELLED]: 'text-red-600',
};

/**
 * UI Variant Enum
 * Used for styling invoice cards based on status
 */
export enum INVOICE_UI_VARIANT {
  UNPAID = 'unpaid',
  PAID = 'paid',
  PARTIAL = 'partial',
  OVERDUE = 'overdue',
  OVERDUE_PARTIAL = 'overdue_partial',
  CANCELLED = 'cancelled',
}

/**
 * CVA-3603: Discount Ledger — institute-level subscription/billing discount
 * campaigns (`ScheduledDiscount`). Unrelated to the per-student fee-template
 * discount feature elsewhere in the app — do not conflate the two.
 *
 * Matches the backend `DISCOUNT_TYPE` Prisma enum exactly.
 */
export enum DISCOUNT_TYPE {
  PERCENTAGE = 'PERCENTAGE',
  FLAT_AMOUNT = 'FLAT_AMOUNT',
  PER_STUDENT = 'PER_STUDENT',
}

/**
 * Display label for a discount campaign type. A function, not a static
 * lookup object — a module-level `const` built by calling `tr()` once at
 * import time freezes at whatever language was active on first load and
 * never updates on a live in-session language switch (caught via a real
 * Playwright pass switching the app to Arabic mid-session). Call this fresh
 * at render time instead.
 */
export const getDiscountTypeLabel = (type: DISCOUNT_TYPE): string => {
  switch (type) {
    case DISCOUNT_TYPE.PERCENTAGE:
      return tr('discount_ledger.type_percentage', 'Percentage');
    case DISCOUNT_TYPE.FLAT_AMOUNT:
      return tr('discount_ledger.type_flat_amount', 'Flat Amount');
    case DISCOUNT_TYPE.PER_STUDENT:
      return tr('discount_ledger.type_per_student', 'Per Student');
    default:
      return type;
  }
};

/**
 * Matches the backend `DISCOUNT_SCOPE` Prisma enum exactly. INSTITUTE_LEVEL
 * is a recurring, cron-generated campaign; INVOICE_LEVEL is a one-shot
 * Manual Settlement tied to a single target invoice (CVA-2975/CVA-4443).
 */
export enum DISCOUNT_SCOPE {
  INSTITUTE_LEVEL = 'INSTITUTE_LEVEL',
  INVOICE_LEVEL = 'INVOICE_LEVEL',
}

/**
 * Matches the backend `DISCOUNT_STATUS` Prisma enum exactly. Per the CVA-3603
 * planning decision, the ledger shows all raw states rather than a collapsed
 * Active/Inactive badge. CVA-3604 replaces CANCELLED (a dead value, never
 * set by any code path) with PAUSED (Pause/Resume Institute Discount).
 */
export enum DISCOUNT_STATUS {
  PENDING_CONVERSION = 'PENDING_CONVERSION',
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
  COMPLETED = 'COMPLETED',
}

/**
 * Display label for a discount campaign status. A function, not a static
 * lookup object — see `getDiscountTypeLabel`'s comment above for why: a
 * module-level `const` built via `tr()` at import time never updates on a
 * live in-session language switch. Call this fresh at render time instead.
 */
export const getDiscountStatusLabel = (status: DISCOUNT_STATUS): string => {
  switch (status) {
    case DISCOUNT_STATUS.PENDING_CONVERSION:
      return tr('discount_ledger.status_pending_conversion', 'Pending Conversion');
    case DISCOUNT_STATUS.ACTIVE:
      return tr('discount_ledger.status_active', 'Active');
    case DISCOUNT_STATUS.PAUSED:
      return tr('discount_ledger.status_paused', 'Paused');
    case DISCOUNT_STATUS.COMPLETED:
      return tr('discount_ledger.status_completed', 'Completed');
    default:
      return status;
  }
};

/**
 * CVA-4443: Institute-Level Discount (Create) — matches the backend
 * `DISCOUNT_DURATION_TYPE` Prisma enum exactly. Controls whether a
 * `ScheduledDiscount` runs for a fixed number of billing cycles or a
 * fixed calendar date range.
 */
export enum DISCOUNT_DURATION_TYPE {
  CYCLE_COUNT = 'CYCLE_COUNT',
  DATE_RANGE = 'DATE_RANGE',
}

/**
 * Display labels for the duration-type toggle in the Create Institute
 * Discount modal (Settlement tab).
 */
export const DISCOUNT_DURATION_TYPE_LABELS: Record<DISCOUNT_DURATION_TYPE, string> = {
  [DISCOUNT_DURATION_TYPE.CYCLE_COUNT]: tr('create_discount.duration_cycle_count', 'Cycle Count'),
  [DISCOUNT_DURATION_TYPE.DATE_RANGE]: tr('create_discount.duration_date_range', 'Date Range'),
};
